import { ApolloError } from "@apollo/client";
import { add } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import {
  EnumProductType,
  useOrderCreateMutation,
  OrderCreateMutationVariables,
  StorefrontCalendarFeedDocument,
  BookerManyQuery,
} from "gql/generated";
import i18n, { i18nNamespace, loadLanguagePacks } from "i18n";
import { useCallback } from "react";
import { useFormHandler } from "shared/hooks";
import { z } from "zod";

// Ensure the required language packs are loaded
await loadLanguagePacks([i18nNamespace.Storefront]);

const manualOrderFormSchema = z
  .object({
    mainProductType: z.string(),
    upsellProductIds: z.array(z.string()),
    firstName: z
      .string()
      .min(1, i18n.t("storefront:CALENDAR.MANUAL_ORDER.ERRORS.FIRST_NAME")),
    lastName: z
      .string()
      .min(1, i18n.t("storefront:CALENDAR.MANUAL_ORDER.ERRORS.LAST_NAME")),
    email: z
      .string()
      .min(1, i18n.t("storefront:CALENDAR.MANUAL_ORDER.ERRORS.EMAIL"))
      .email(i18n.t("storefront:CALENDAR.MANUAL_ORDER.ERRORS.EMAIL_VALID")),
    startISO: z.string(),
    endISO: z.string(),
    bookerId: z
      .string()
      .min(1, i18n.t("storefront:CALENDAR.MANUAL_ORDER.ERRORS.BOOKER_ID")),
  })
  .superRefine((data, ctx) => {
    if (data.mainProductType === "ACCOMMODATION") {
      if (data.startISO.length < 1 || data.endISO.length < 1) {
        ctx.addIssue({
          message: i18n.t("storefront:CALENDAR.MANUAL_ORDER.ERRORS.DATES"),
          path: ["startISO"],
          code: "custom",
        });
      }
    }
  });

export type ManualOrderFormValues = z.infer<typeof manualOrderFormSchema>;

interface ManualOrderFormOptions {
  onSuccess?: (orderID: string) => void;
  onError?: (message: string) => void;
  storefrontId: string;
  screen: "build" | "review";
  bookers: BookerManyQuery["bookerMany"];
  firstName?: string;
  lastName?: string;
  email?: string;
}

export const useManualOrderForm = ({
  onSuccess,
  onError,
  storefrontId,
  screen,
  bookers,
  firstName,
  lastName,
  email,
}: ManualOrderFormOptions) => {
  const defaultValues: ManualOrderFormValues = {
    mainProductType: EnumProductType.Fee,
    firstName: firstName ?? "",
    lastName: lastName ?? "",
    email: email ?? "",
    startISO: "",
    endISO: "",
    upsellProductIds: [],
    bookerId: "",
  };

  const [createOrder] = useOrderCreateMutation();

  return useFormHandler<ManualOrderFormValues>(
    useCallback(
      async data => {
        if (screen === "review" && data) {
          const upsellPurchaseRequests =
            data.upsellProductIds?.map(id => ({
              productId: id ?? "",
              startDateISO: "",
              endDateISO: "",
              guestTiers: [],
            })) ?? [];

          const mainProductId = bookers.find(b => b._id === data.bookerId)
            ?.bundles[0]?.product?._id;

          const payload: OrderCreateMutationVariables = {
            source: { booker: data.bookerId ?? "", storefront: storefrontId },
            customer: {
              email: data.email ?? "",
              firstName: data.firstName ?? "",
              lastName: data.lastName ?? "",
              phone: "",
            },
            guests: [],
            paymentMethodId: "manual_order",
            productPurchaseRequests: [
              {
                productId: mainProductId ?? "",
                startDateISO:
                  data.startISO && data.startISO.length > 0
                    ? add(toZonedTime(data.startISO, "UTC"), {
                        hours: 1,
                      }).toISOString()
                    : "",
                endDateISO:
                  data.endISO && data.endISO.length > 0
                    ? toZonedTime(data.endISO, "UTC").toISOString()
                    : "",
                guestTiers: [],
              },
            ].concat(upsellPurchaseRequests),
          };

          try {
            const res = await createOrder({
              variables: payload,
              refetchQueries: [StorefrontCalendarFeedDocument],
            });

            if (res.data && res.data?.orderCreate?.record?._id) {
              onSuccess?.(res.data.orderCreate.record._id);
            }
          } catch (error) {
            if (error instanceof ApolloError) {
              onError?.(
                error.message.includes(
                  "No accommodation inventory item is available",
                )
                  ? i18n.t(
                      "storefront:CALENDAR.MANUAL_ORDER.ERRORS.UNAVAILABLE_DATES",
                    )
                  : i18n.t(
                      "storefront:CALENDAR.MANUAL_ORDER.ERRORS.FAILED_TO_CREATE",
                    ),
              );
            }
          }
        }
      },
      [createOrder, onError, onSuccess, storefrontId, screen, bookers],
    ),
    defaultValues,
    { schema: manualOrderFormSchema },
  );
};
